'use client'

import { type ReactNode, useEffect, useRef, type FC, type PropsWithChildren, isValidElement } from 'react'
import { animate, motion } from 'framer-motion'
import Tabs from '../Tabs'
import Button from '../Button'
import { type CommonProps } from '@/utils/types'
import Link from 'next/link'
import useSearchParamState from '../../hooks/useSearchParamState'
import clsx from 'clsx'
import NextImage from 'next/image'
import { getBackgroundImageUrl, type TabStateValue, bgImages } from './utils'
import TitleSvg from './TitleSvg'
import handshake from '../../../public/icon-handshake.svg'
import security from '../../../public/icon-security.svg'
import chart from '../../../public/icon-pulse-chart.svg'
import people from '../../../public/icon-people.svg'

/**
 * The homepage tabs component.
 * Displays different content sections with animated background images.
 *
 * If provided, the `bgElementId` will be used to find the element whose background should change.
 * The `elementQuery` is optional and defaults to `data-changable-background`.
 */
const HomepageTabs: FC<HomepageTabsProps> = ({ bgElementId, elementQuery = 'data-changable-background' }) => {
  const [value, setQueryValue] = useSearchParamState<TabStateValue>('tab', 'tab-1')
  const skipAnimation = useRef(true)

  /**
   * Effect to handle background image changes when tab changes.
   */
  useEffect(() => {
    const el = document.querySelector<HTMLDivElement>(`[${elementQuery}="${bgElementId}"]`)
    if (!el) return

    const nextBackground = getBackgroundImageUrl(value ?? 'tab-1')
    el.style.backgroundImage = `url(${nextBackground})`
    el.style.backgroundPosition = 'right center'
    el.style.backgroundSize = '50%'
    el.style.backgroundRepeat = 'no-repeat'

    if (skipAnimation.current) {
      skipAnimation.current = false
      return
    }

    animate(el, {
      opacity: [0.5, 1]
    }, {
      duration: 0.5,
      ease: 'easeInOut'
    })
  }, [bgElementId, elementQuery, value])

  /**
   * Effect to preload background images
   */
  useEffect(() => {
    // Preload all background images
    Object.values(bgImages).forEach((src) => {
      const img = new Image()
      img.src = `/${src}`
    })
  }, [])

  return (
    <Tabs value={value} onValueChange={value => setQueryValue(value as TabStateValue)} className="my-[6rem] min-h-[36rem]">

      <Tabs.List>
        <Tabs.Trigger value="tab-1">Ocean Impact Initiative</Tabs.Trigger>
        <Tabs.Trigger value="tab-2">What is the OII?</Tabs.Trigger>
        <Tabs.Trigger value="tab-3">Distinctive Factors</Tabs.Trigger>
      </Tabs.List>

      <Tabs.Content value="tab-1">
        <TabContent title={<TitleSvg className="my-6" />} big className="max-w-[40rem] mt-6">
          <p className="leading-[1.4]">
            The first global science-based framework designed to address the direct
            and indirect impacts of various business sectors on marine ecosystems
          </p>
          <div className="w-full mt-8">
            <Button variant="accent" asChild>
              <Link href="/login" aria-label="Access the data">
                Access the data
              </Link>
            </Button>
          </div>
        </TabContent>
      </Tabs.Content>

      <Tabs.Content value="tab-2">
        <TabContent title="What is in the OII's data?" className="max-w-[50rem] mt-8">
          <p>The OII focuses on main managerial domains:</p>

          <ul className="grid grid-cols-12 mt-6">
            <IconListItems icon={handshake} delay={0.1} className="col-span-5 my-3">
              Governance & Policies
            </IconListItems>
            <IconListItems icon={security} delay={0.2} className="col-span-7 my-3">
              Pressure, Dependence & Risk Assessment
            </IconListItems>
            <IconListItems icon={chart} delay={0.3} className="col-span-5 my-3">
              Role & Measurement
            </IconListItems>
            <IconListItems icon={people} delay={0.4} className="col-span-7 my-3">
              Managerial Activation
            </IconListItems>
          </ul>

          <p className="mt-7">These domains are analysed across three ocean dimensions:</p>

          <ul className="list-disc my-4 pl-6 space-y-2.5">
            <li>Marine biological integrity and diversity;</li>
            <li>Marine water contamination and chemical conditions integrity;</li>
            <li>Seawater and/or seabed physical conditions integrity.</li>
          </ul>

          <p className="mt-7 leading-[1.4]">
            The data collected is specific to each company, providing insights
            <br />
            into their impact and positioning within their respective industries.
          </p>
        </TabContent>
      </Tabs.Content>

      <Tabs.Content value="tab-3">
        <TabContent title="Distinctive Factors" className="mt-7">
          <p>
            The OII has the following key features:
          </p>

          <ul className="list-disc my-4 pl-6 space-y-2.5">
            <li>First data provider for the financial community on ocean impact</li>
            <li>Grounded in scientific methodologies</li>
            <li>Comprehensive and sector-specific</li>
            <li>AI-Driven, leverages GenAI for efficient data analysis</li>
            <li>Aligned with main international frameworks</li>
          </ul>

          <div className="w-full gap-4 flex pt-10">
            <Button asChild>
              <Link href="/sectors" aria-label="Sectors" prefetch>
                Sectors
              </Link>
            </Button>
            <Button asChild>
              <Link href="/results" aria-label="Results" prefetch>
                Results
              </Link>
            </Button>
            <Button asChild>
              <Link href="/login" aria-label="Get access to the data" prefetch>
                Get access to the data
              </Link>
            </Button>
          </div>
        </TabContent>
      </Tabs.Content>
    </Tabs>
  )
}

const TabContent: FC<PropsWithChildren<{ className?: string, title: ReactNode, big?: boolean }>> = (props) => {
  const { className, title, big, children } = props
  const classList = clsx('flex flex-col gap-6 animate-in fade-in slide-in-from-bottom-10', className)

  return (
    <div className={classList}>
      {isValidElement(title)
        ? title
        : (
            <h1
              className={clsx(
                'font-raleway font-bold',
                big ? 'text-[86px] leading-[1.05] tracking-wider max-w-[28rem]' : 'text-[56px] leading-none'
              )}
            >
              {title}
            </h1>
          )}
      <div className="text-[1.25rem] leading-[1.2]">
        {children}
      </div>
    </div>
  )
}

const IconListItems: FC<PropsWithChildren<CommonProps & { icon: string, delay?: number }>> = (props) => {
  const { children, icon, delay, className } = props
  const classList = clsx('flex items-center gap-4', className)
  const anim = {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    transition: { delay }
  }

  return (
    <motion.li {...anim} className={classList}>
      <NextImage
        src={icon}
        alt=""
        width={30}
        height={30}
      />
      {children}
    </motion.li>
  )
}
export interface HomepageTabsProps extends CommonProps {
  /**
   * The ID of the element whose background should change.
   */
  bgElementId: string
  /**
   * The optional CSS query attribute to find the background element.
   * Defaults to `data-changable-background`.
   */
  elementQuery?: string
}

export default HomepageTabs
